import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import {
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	InputGroup,
	InputLeftElement,
	SimpleGrid,
} from '@tempo/core';
import {useToast} from '@deezer/next/shared/hooks/useToast';
import {ArrowRightIcon, InstagramIcon, FacebookIcon, XIcon} from '@tempo/icons';
import type {ManagerArtist, ManagerFormData} from '../../types';
import type {ArtistSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import ArtistSearchInput from '../../components/ArtistSearchInput';
import UPCInfoTooltip from '../../components/UPCInfoTooltip';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {ApiErrorCode} from '@constants/errorCodes';
import {isErrorCode} from '@deezer/next/services/api/utils';
import {StakeholderType} from '@deezer/next/services/api/Starmania/requests/album/types';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';

type AddEditArtistManagerFormCardProps = {
	formData: ManagerFormData;
	selectedArtist?: ManagerArtist;
	edit: boolean;
	onBack: () => void;
	onSave: (artist: ManagerArtist, isEdition: boolean) => void;
};

const AddEditManagerArtistFormCard = ({
	formData,
	selectedArtist,
	edit,
	onBack,
	onSave,
}: AddEditArtistManagerFormCardProps) => {
	const {t} = useTranslation('d4c');
	const selectedArtistIds = useMemo(
		() => formData.managerArtists.map(({id}) => id),
		[formData.managerArtists],
	);
	const [upcError, setUPCError] = useState<string>();
	const [artist, setArtist] = useState<
		Omit<ArtistSearchResult, 'redirectUrl'> | undefined
	>(selectedArtist);
	const [upc, setUPC] = useState(selectedArtist?.upc || '');
	const [instagram, setInstagram] = useState(selectedArtist?.instagram || '');
	const [facebook, setFacebook] = useState(selectedArtist?.facebook || '');
	const [twitter, setTwitter] = useState(selectedArtist?.twitter || '');
	const [website, setWebsite] = useState(selectedArtist?.website || '');
	const toast = useToast();

	const handleSelectedArtistChange = useCallback(
		(item?: ArtistSearchResult) => {
			setUPC('');
			setInstagram('');
			setFacebook('');
			setTwitter('');
			setWebsite('');
			setUPCError(undefined);
			setArtist(item);
		},
		[],
	);

	const handleUPCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUPC(e.target.value);
		setUPCError(undefined);
	};

	const handleInstagramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInstagram(e.target.value);
	};

	const handleFacebookChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFacebook(e.target.value);
	};

	const handleTwitterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setTwitter(e.target.value);
	};

	const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setWebsite(e.target.value);
	};

	const handleSave = async () => {
		if (!artist) {
			return;
		}

		try {
			await StarmaniaApi.album.verifyAlbumUpc(
				upc,
				artist.id,
				StakeholderType.ARTIST,
			);
			onSave(
				{
					...artist,
					upc,
					instagram,
					facebook,
					twitter,
					website,
				},
				edit,
			);
		} catch (e: any) {
			if (
				isErrorCode(e.response.data.errors?.[0]?.code, [
					ApiErrorCode.INVALID_UPC,
					ApiErrorCode.NOT_FOUND_UPC,
				])
			) {
				setUPCError(
					t('toastmessage_text_upcinvalidordoesntbelongtoXname_d4c-web', {
						sprintf: [artist.name],
					}),
				);
			} else {
				toast({
					description:
						e.response?.data?.errors?.[0]?.details ||
						t('toastmessage_text_unknownerroroccurred_d4c-web'),
					status: 'error',
				});
			}
		}
	};

	return (
		<PageLayout
			data-testid={`${edit ? 'edit' : 'add'}-manager-artist-card`}
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							<Button
								data-testid="back-button"
								variant="outline"
								onClick={onBack}
								leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // todo fix that
								size="small"
							>
								{t('generic_action_back_d4c-web')}
							</Button>
							<Button
								data-testid="save-artist-button"
								isDisabled={!artist || !artist.id || !upc || Boolean(upcError)}
								onClick={handleSave}
								size="small"
							>
								{t('generic_action_save_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<FormSectionTitle
						title={
							edit
								? t('requestaccessmanager_title_editartist_d4c-web')
								: t('requestaccessmanager_title_addnewartist_d4c-web')
						}
						// fixme: translate
						subtitle="Any discography problem ?"
						subLink="https://support.deezer.com"
					/>
					<FormSectionContainer>
						<FormControl isRequired={true}>
							<FormLabel htmlFor="artist-name-search">
								{/*fixme: translate*/}
								{/*{t('requestaccessartist_text_artistname_d4c-web')}*/}
							</FormLabel>
							<ArtistSearchInput
								data-testid="claim-new-manager-search"
								id="artist-name-search"
								// fixme:translate
								placeholder="Enter artist id"
								selectedArtist={artist}
								onSelectedArtistChange={handleSelectedArtistChange}
								filterArtistIds={selectedArtistIds}
								isDisabled={edit}
								displayArtistId={true}
							/>
						</FormControl>
						<FormControl
							isRequired={true}
							isDisabled={!artist}
							isInvalid={Boolean(upcError)}
						>
							<FormLabel htmlFor="upc">
								{t('deliverymonitoring_text_upc_d4c-web')}
								<UPCInfoTooltip top="-1px" ms={4} position="absolute" />
							</FormLabel>
							<Input
								data-testid="claim-new-manager-upc"
								id="upc"
								type="text"
								value={upc}
								onChange={handleUPCChange}
							/>
							<FormErrorMessage>{upcError}</FormErrorMessage>
						</FormControl>
						<SimpleGrid columns={{base: 1, md: 2}} spacing="spacing.l">
							<FormControl>
								<FormLabel htmlFor="instagram">Instagram</FormLabel>
								<InputGroup>
									<InputLeftElement pointerEvents="none">
										<InstagramIcon boxSize={6} />
									</InputLeftElement>
									<Input
										id="instagram"
										type="text"
										placeholder="@"
										value={instagram}
										onChange={handleInstagramChange}
									/>
								</InputGroup>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="facebook">Facebook</FormLabel>
								<InputGroup>
									<InputLeftElement pointerEvents="none">
										<FacebookIcon boxSize={6} />
									</InputLeftElement>
									<Input
										id="facebook"
										type="text"
										value={facebook}
										onChange={handleFacebookChange}
									/>
								</InputGroup>
							</FormControl>
							<FormControl>
								{/* TODO migrate to X */}
								<FormLabel htmlFor="twitter">Twitter</FormLabel>
								<InputGroup>
									<InputLeftElement pointerEvents="none">
										<XIcon boxSize={6} />
									</InputLeftElement>
									<Input
										id="twitter"
										type="text"
										placeholder="twitter.com"
										value={twitter}
										onChange={handleTwitterChange}
									/>
								</InputGroup>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="website">
									{t('requestaccesslabel_text_website_d4c-web')}
								</FormLabel>
								<Input
									id="website"
									type="text"
									placeholder="https://"
									value={website}
									onChange={handleWebsiteChange}
								/>
							</FormControl>
						</SimpleGrid>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default AddEditManagerArtistFormCard;
